import { emptyQuoteLocation, isResolvedLocation, isValidQuote, isMexicoQuote } from "@/lib/services/QuoteLocation"
import React, { Dispatch, FC, forwardRef, SetStateAction, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { api } from "@/lib/api"
import { Geo, Quote } from "@/lib/types"
import { useRouter } from "next/router"
import { Localstorage } from "@/lib/services"
import { LocalStorage } from "@/lib/types/LocalStorage"

export type Props = {
  isCheck?: boolean
  isSwap?: boolean
  handleForm?: (form: Quote) => void
  setQuote?: Dispatch<SetStateAction<Quote>>
  title?: string
  getData?: (form: Geo) => void
  setLatLng?: (form: Geo) => void
  setGeoLocations?: (form: Geo) => void
  copyData?: Quote
  loading?: boolean
  setIsEmpty?: (isEmpty: boolean) => void
  setInputEmpty?: (inputEmpty: boolean) => void
  setInputEmptySales?: (inputEmpty: boolean) => void
  tabIndex?: number
  mexico?: boolean
  geoLocations?: {
    origin: Geo["geoLocations"]
    destination: Geo["geoLocations"]
  }
  setData?: (data: {
    city: string
    state: string
    zip: string
    country: string
    latLng: {
      lat: number
      lng: number
    }
  }) => void
  sportRate?: boolean
}

export const Location = forwardRef<HTMLInputElement | null, Props>((props, ref) => {
  const [isCheck, setIsCheck] = useState(false)
  const [apiCallMade, setApiCallMade] = useState(false)
  const findPlaceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputValueRef = useRef("")
  const DEBOUNCE_MS = 400
  const { pathname } = useRouter()
  const reQuote = Localstorage.get<Quote>("re_quote")
  const mutation = useMutation((payload: { input: string }) => {
    return api.PlacesApi.FindPlace(payload)
  })
  const user = Localstorage.get<LocalStorage>("next.user")
  const [showOptions, setShowOptions] = useState<boolean>(mutation.isSuccess)

  const locationRevision = useRef(0)
  const [locationError, setLocationError] = useState("")
  const publishLocation = (form: Geo) => {
    props.setLatLng?.(form)
    props.getData?.(form)
    props.setGeoLocations?.(form)
    const geo = form.geoLocations
    props.setData?.({ city: geo.city, state: geo.state, zip: geo.zip,
      country: geo.country || "", latLng: geo.location })
  }
  const invalidateLocation = () => {
    locationRevision.current += 1
    setLocationError("")
    publishLocation({ status: "Pending", input: "", geoLocations: emptyQuoteLocation() })
    props.setIsEmpty?.(false)
    props.setInputEmpty?.(true)
    props.setInputEmptySales?.(true)
  }
  useEffect(() => () => { locationRevision.current += 1 }, [])
  const mutationGeo = useMutation(
    (payload: { place_id: string; revision: number }) => api.PlacesApi.ReverseGeo({ place_id: payload.place_id }),
    {
      onSuccess(form, request) {
        if (request.revision !== locationRevision.current) return
        if (!isResolvedLocation(form.geoLocations)) {
          setLocationError("Please select a more specific address from the suggestions.")
          return
        }
        setValue("city", form.geoLocations.city)
        setValue("state", form.geoLocations.state)
        setValue("zip", `${form.geoLocations.city} ${form.geoLocations.state}, ${form.geoLocations.zip}`)
        publishLocation(form)
        props.setInputEmpty?.(false)
        props.setInputEmptySales?.(false)
      },
      onError(error: { message: string }, request) {
        if (request.revision === locationRevision.current) setLocationError(error.message)
      },
    },
  )
  const selectPlace = (placeId: string) => {
    invalidateLocation()
    mutationGeo.mutate({ place_id: placeId, revision: locationRevision.current })
    setShowOptions(false)
    setIsCheck(true)
  }

  const { register, setValue, reset } = useForm<{
    zip: string
    city: string
    state: string
  }>()

  useEffect(() => {
    if (props.isSwap && props.isCheck == false && props.geoLocations) {
      setValue(
        "zip",
        `${props.geoLocations?.destination.city} ${props.geoLocations?.destination.state}, ${props.geoLocations?.destination.zip}` ||
        "",
      )
    }
    if (props.isSwap && props.isCheck == true && props.geoLocations) {
      setValue(
        "zip",
        `${props.geoLocations?.origin.city} ${props.geoLocations?.origin.state}, ${props.geoLocations?.origin.zip}` ||
        "",
      )
    }
  }, [props.geoLocations, props.isSwap, props.isCheck])


  useEffect(() => {
    if (reQuote && props.isCheck) {
      const city = reQuote?.originLatLng?.city || ""
      const state = reQuote?.originLatLng?.state || ""
      const zip = reQuote?.originZip || ""

      const formattedZip = `${city} ${state}${state ? "," : ""} ${zip}`.trim()
      setValue("zip", formattedZip)
    } else {
      const city = reQuote?.destinationLatLng?.city || ""
      const state = reQuote?.destinationLatLng?.state || ""
      const zip = reQuote?.destinationZip || ""

      const formattedZip = `${city} ${state}${state ? "," : ""} ${zip}`.trim()
      setValue("zip", formattedZip)
    }
  }, [])

  useEffect(() => {
    setShowOptions(mutation.isSuccess)
  }, [mutation.isSuccess])

  const findPlace = (location: string) => {
    const input = {
      input: location,
    }
    mutation.mutate(input)
  }

  useEffect(() => {
    return () => {
      if (findPlaceDebounceRef.current) clearTimeout(findPlaceDebounceRef.current)
    }
  }, [])

  useEffect(() => {
    if (
      props.isCheck === true &&
      reQuote &&
      props.copyData &&
      props.copyData.destinationZip &&
      props.copyData.originZip &&
      props.copyData.equipmentType &&
      apiCallMade == false
      && props.geoLocations
      && isResolvedLocation(props.geoLocations.origin)
      && isResolvedLocation(props.geoLocations.destination)
    ) {
      const request = {
        ...props.copyData,
        originLatLng: props.geoLocations.origin,
        destinationLatLng: props.geoLocations.destination,
      }
      request.mx = isMexicoQuote(request)
      if (!isValidQuote(request)) return
      setApiCallMade(true)
      setShowOptions(false)
      props.setQuote?.(request)
      props.handleForm?.(request)
    }
  }, [
    props.copyData,
    isCheck,
    apiCallMade,
    props.handleForm,
    props.geoLocations,
  ])

  const handleKeyPress = (e: any, placeId: string) => {
    if (e.key === "Enter") {
      e.preventDefault()
      selectPlace(placeId)
    }
  }


  return (
    <div className="relative">
      <h1
        className={
          (pathname !== "/spot-rate" ? "pt-5" : "") +
          " dark:text-primary-10 text-light-2 2xl:text-xl text-2xl"
        }
      >
        {props.title}
      </h1>
      <div className="grid grid-cols-1 gap-3 mt-5">
        <fieldset>
          <input
            tabIndex={props.tabIndex}
            className="w-full border outline-none dark:bg-transparent bg-white border-primary-8 shadow-sm rounded-md px-3 py-2 dark:text-primary-10 text-light-2 h-10 box-shadow text-sm  placeholder:text-primary-19 placeholder:text-xs"
            type="text"
            placeholder="Enter City, State and Zip code"
            {...register("zip", {
              onChange(event) {
                const value = event.target.value
                invalidateLocation()
                inputValueRef.current = value

                if (value.length > 2) {
                  if (findPlaceDebounceRef.current) clearTimeout(findPlaceDebounceRef.current)
                  findPlaceDebounceRef.current = setTimeout(() => {
                    findPlace(inputValueRef.current)
                    findPlaceDebounceRef.current = null
                  }, DEBOUNCE_MS)
                } else {
                  if (findPlaceDebounceRef.current) {
                    clearTimeout(findPlaceDebounceRef.current)
                    findPlaceDebounceRef.current = null
                  }
                  setShowOptions(false)
                }

                props.setIsEmpty?.(false)
              },
            })}
            ref={(e) => {
              if (typeof ref === "function") {
                ref(e)
              } else if (ref) {
                (ref as React.MutableRefObject<HTMLInputElement | null>).current = e
              }
              register("zip").ref(e)
            }}
          />
        </fieldset>
      </div>
      {locationError && <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{locationError}</p>}
      {showOptions && mutation.variables?.input === inputValueRef.current && (
        <div
          className={
            (props.sportRate ? "" : "absolute") +
            " z-10  w-full max-h-40 mt-1 overflow-auto dark:bg-primary-1 bg-white font-normal focus:outline-none rounded-md border border-primary-8 shadow-sm divide-y dark:divide-zinc-800"
          }
        >
          {mutation.data == undefined
            ? null
            : mutation.data.completeResults.map((e: any, i: number) => (
              <div
                key={i}
                onKeyPress={(event) => handleKeyPress(event, e.place_id)}
                tabIndex={props.tabIndex}
                className="relative dark:text-primary-10 text-light-2 dark:hover:bg-zinc-800 hover:bg-gray-300 cursor-pointer focus:bg-white/5 focus:text-primary-3"
                onClick={() => {
                  selectPlace(e.place_id)
                }}
              >
                <span className="block truncate text-sm font-normal p-2">
                  {e.description}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
})
Location.displayName = "Location"